import React, { useEffect, useMemo } from 'react'
import { isEqual, toString } from 'lodash'
import { useParams, useHistory, Prompt, Redirect, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'

import { Loader } from '@/components/loaders/Loader'
import { fetchPlaybook } from '@/reducers/playbooks/playbook.actions'
import { massPlaybookUpdateReset } from '@/reducers/playbooks/playbooks.redux'
import { updatePlaybookName } from '@/reducers/playbooks/playbook.redux'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Button } from '@/components/buttons/Button'
import { EditableHeader } from '@/components/headers/EditableHeader'

import { playbookTitlesSectionMap, sections } from './playbook.helpers'
import { PlaybookTabs } from './components/PlaybookTabs'
import { PlaybookScorecardWarning } from './components/PlaybookScorecardWarning'
import { CategoryDisplay } from './modules/categories/CategoryDisplay'
import { CategoryEntryEditor } from './modules/categories/CategoryEntryEditor'
import { NestedChecklistDisplay } from './modules/checklist/NestedChecklistDisplay'
import { ChecklistTabs } from './modules/checklist/ChecklistTabs'
import { NotificationsEditor } from './modules/notifications/NotificationsEditor'
import { StickyNoteEditor } from './modules/sticky-note/StickyNoteEditor'
import { PlaybookEntryDisplay } from './components/PlaybookEntryDisplay'
import { PlaybookSubmitButton } from './components/PlaybookSubmitButton'
import { PlaybookVersionSelector } from './components/PlaybookVersionSelector'

import './Playbooks.scss'

export const sectionData = [
  {
    route: sections.CHECKLIST,
    components: { display: NestedChecklistDisplay, editor: ChecklistTabs },
  },
  {
    route: sections.DYNAMIC_PROMPT,
    components: { display: CategoryDisplay, editor: CategoryEntryEditor },
  },
  {
    route: sections.NOTIFICATION,
    components: { display: PlaybookEntryDisplay, editor: NotificationsEditor },
  },
  {
    route: sections.STICKY_NOTE,
    components: { display: PlaybookEntryDisplay, editor: StickyNoteEditor },
  },
  {
    route: sections.POSTCALL,
    components: { display: CategoryDisplay, editor: CategoryEntryEditor },
  },
]

export function renderSection(section, showEditor, flags) {
  const sectionType = showEditor ? 'editor' : 'display'
  const routeFound = sectionData.find((data) => data.route === section)

  if (routeFound) {
    const Component = routeFound.components[sectionType]

    return <Component flags={flags} />
  }

  return <div />
}

const PlaybookEditorPage = ({ flags }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { id, section, entryId } = useParams()
  const {
    loading = false,
    playbook,
    playbook: { id: playbookId, current: isLatestPlaybookVersion, active, mostRecentId },
    validationError,
    dirty,
  } = useSelector((state) => state.playbook)
  const { organizationid: organizationId, edit_config } = useSelector((state) => state.currentUser)
  const { scorecardAssociations } = useSelector((state) => state.scorecards.data)
  const { massPlaybookUpdate } = useSelector((state) => state.playbooks)

  // If user has landed on the old playbook editor URL, redirect to the new
  if (!section) {
    return <Redirect to={`/playbooks/${id}/checklist`} />
  }

  // Individual drill down detail editing page
  // Navigation and title is hidden, and editors are displayed instead of lists of entries
  const isEditorDetailPage = !!entryId
  const isTabPage = !!entryId && isEqual(section, 'checklist')
  const isCreate = entryId === 'create'
  const sectionLink = `/playbooks/${massPlaybookUpdate.newPlaybookVersion ?? id}/${section}`
  const readOnly = !isLatestPlaybookVersion || !edit_config

  const handleUpdatePlaybookName = (_, values) => {
    dispatch(updatePlaybookName(values))
  }

  const getNumberOfBreakingChangesScorecards = (scorecardAssociations) => {
    let numOfBreakingScorecards = 0
    scorecardAssociations?.forEach((scorecard) => {
      if (scorecard.criteria_not_in_playbook.length > 0) {
        numOfBreakingScorecards += 1
      }
    })
    return numOfBreakingScorecards
  }

  const numberOfBreakingScorecards = useMemo(
    () => getNumberOfBreakingChangesScorecards(scorecardAssociations),
    [scorecardAssociations]
  )

  const shouldShowScorecardWarning = numberOfBreakingScorecards > 0 && !active

  useEffect(() => {
    // Fetch new playbook and scorecard details if URL changes
    if (toString(id) !== toString(playbookId)) {
      dispatch(fetchPlaybook(id, organizationId))
      dispatch(massPlaybookUpdateReset())
    }
  }, [id, playbookId, organizationId])

  // Show a message if the user tries to close the window
  useEffect(() => {
    if (dirty) {
      window.onbeforeunload = () => {
        return true
      }
    }

    return () => {
      window.onbeforeunload = null
    }
  }, [dirty])

  return (
    <>
      <Prompt
        when={dirty || (isLatestPlaybookVersion && !dirty && !active)}
        message={(nextLocation) => {
          if (nextLocation.pathname.startsWith('/playbooks/')) {
            return true
          }
          return `You have ${
            dirty ? 'unsaved' : 'unpublished'
          } changes. Are you sure you want to leave?`
        }}
      />

      {loading.playbook ? (
        <Loader centered data-testid="loading" />
      ) : (
        <>
          <header
            className="page-header playbook-page-header"
            data-testid={`playbook-editor-page-${section}`}
          >
            <nav className="playbook-breadcrumb" data-testid="breadcrumbs">
              <Link to="/playbooks" className="playbook-breadcrumb__crumb">
                Playbooks
              </Link>
              <span className="playbook-breadcrumb__divider">/</span>
              {isEditorDetailPage ? (
                <>
                  <Link
                    to={sectionLink}
                    className="playbook-breadcrumb__crumb playbook-breadcrumb__crumb--truncate"
                  >
                    {playbook.name}
                  </Link>
                  <span className="playbook-breadcrumb__divider">/</span>
                  <h1 className="playbook-title">{`${isCreate ? 'Add New' : 'Edit'} ${playbookTitlesSectionMap[section]}`}</h1>
                </>
              ) : readOnly ? (
                <h1 className="playbook-title">{playbook.name} [Read Only]</h1>
              ) : (
                <EditableHeader initialName={playbook.name} updateName={handleUpdatePlaybookName} />
              )}
            </nav>
            <div>
              {!isEditorDetailPage && (
                <div className="playbook-page-header__actions">
                  <PlaybookVersionSelector />
                  <PlaybookSubmitButton numberOfBreakingScorecards={numberOfBreakingScorecards} />
                </div>
              )}
              {dirty && (
                <ErrorMessage
                  style={{ justifyContent: 'flex-end', marginTop: '1rem' }}
                  content="You have unsaved changes!"
                />
              )}
            </div>
          </header>

          {!isEditorDetailPage && (
            <>
              {shouldShowScorecardWarning && (
                <PlaybookScorecardWarning numberOfBreakingScorecards={numberOfBreakingScorecards} />
              )}
              {!isLatestPlaybookVersion && (
                <div className="playbook-switch">
                  <p>This playbook version is out of date.</p>
                  <Button
                    brand
                    onClick={() => {
                      history.push(`/playbooks/${mostRecentId}/checklist`)
                    }}
                  >
                    Go to Active Playbook Version
                  </Button>
                </div>
              )}

              <PlaybookTabs
                playbookId={id}
                section={section}
                validationError={validationError}
                isLatestPlaybookVersion={isLatestPlaybookVersion}
              />
            </>
          )}

          <div className="playbook-container">
            {isEditorDetailPage ? (
              <>
                {isTabPage ? (
                  renderSection(section, true, flags)
                ) : (
                  <div className="playbook-container__editor">
                    {renderSection(section, true, flags)}
                  </div>
                )}
              </>
            ) : (
              <div className="playbook-container__display">
                {renderSection(section, false, flags)}
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}

export default withLDConsumer()(PlaybookEditorPage)
